import React, { useRef } from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { getMedias, getSKUs } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { ProductSearchAutocomplete } from '../../../modules/products/public';
import {
  buildProductMediaEntries,
  createProductItemFromProduct,
  resolvedSkuDisplayLabel,
} from './catalogCompositionUtils';
import CatalogItemMediaEditor from './CatalogItemMediaEditor';

const CatalogProductItemForm = ({ item, onChange, disabled, productTypeFilter }) => {
  const { translate } = useTranslation();
  const pickSeqRef = useRef(0);
  const isNew = !item?.product_id;

  const updateItem = (patchOrFn) => {
    onChange((prev) => {
      const patch = typeof patchOrFn === 'function' ? patchOrFn(prev) : patchOrFn;
      return { ...prev, ...patch };
    });
  };

  const pickProduct = async (product) => {
    if (!product?.id) return;
    const seq = ++pickSeqRef.current;
    let skus = [];
    let allMedias = [];
    try {
      const [skuRes, mediaRes] = await Promise.all([getSKUs(product.id), getMedias()]);
      skus = skuRes?.data?.skus || skuRes?.data || [];
      allMedias = mediaRes?.data?.medias || [];
    } catch {
      skus = [];
      allMedias = [];
    }
    if (seq !== pickSeqRef.current) return;
    const active = (Array.isArray(skus) ? skus : []).filter(
      (s) => (s.status || 'ACTIVE').toUpperCase() === 'ACTIVE',
    );
    const created = createProductItemFromProduct(product, active, item?.display_order ?? 0);
    created.medias = buildProductMediaEntries(allMedias, product.id);
    onChange((prev) => ({ ...created, id: prev?.id || created.id }));
  };

  const toggleSkuMode = (allActive) => {
    onChange((prev) => {
      if (allActive) {
        return { ...prev, sku_selection_mode: 'ALL_ACTIVE', skus: [] };
      }
      const resolved = prev.resolved_skus || [];
      return {
        ...prev,
        sku_selection_mode: 'SELECTED',
        skus: resolved.map((s, i) => ({
          id: '',
          sku_id: s.id,
          price_mode: 'INHERIT',
          override_price: null,
          min_price: null,
          max_price: null,
          display_order: i,
          is_available: true,
          _label: resolvedSkuDisplayLabel(s),
        })),
      };
    });
  };

  const getVariationList = () => {
    if (item.sku_selection_mode === 'SELECTED') {
      return (item.skus || []).map((sk) => ({
        id: sk.sku_id,
        code: sk._label || sk.sku_id,
      }));
    }
    return (item.resolved_skus || []).map((rs) => ({
      id: rs.id,
      code: resolvedSkuDisplayLabel(rs),
    }));
  };

  const toggleVariation = (skuId, selected) => {
    onChange((prev) => {
      const resolved = prev.resolved_skus || [];
      let skus = [...(prev.skus || [])];
      if (selected) {
        if (!skus.find((s) => s.sku_id === skuId)) {
          const rs = resolved.find((r) => r.id === skuId);
          skus.push({
            id: '',
            sku_id: skuId,
            price_mode: 'INHERIT',
            override_price: null,
            min_price: null,
            max_price: null,
            display_order: skus.length,
            is_available: true,
            _label: resolvedSkuDisplayLabel(rs) || skuId,
          });
        }
      } else {
        skus = skus.filter((s) => s.sku_id !== skuId);
      }
      return { ...prev, skus };
    });
  };

  const updateCatalogSku = (skuId, patch) => {
    onChange((prev) => ({
      ...prev,
      skus: (prev.skus || []).map((sk) => (sk.sku_id === skuId ? { ...sk, ...patch } : sk)),
    }));
  };

  if (!item) return null;

  return (
    <Box>
      {isNew && (
        <ProductSearchAutocomplete
          disabled={disabled}
          productTypeFilter={productTypeFilter}
          label={translate('catalog.pick_catalog_product')}
          onChange={(product) => void pickProduct(product)}
          sx={{ mb: 2, width: '100%' }}
        />
      )}

      {item.product_id && (
        <>
          <TextField
            fullWidth
            size="small"
            label={translate('catalog.display_name')}
            value={item.display_name || ''}
            disabled={disabled}
            onChange={(e) => updateItem({ display_name: e.target.value })}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            size="small"
            label={translate('catalog.badge_label')}
            value={item.badge_label || ''}
            disabled={disabled}
            onChange={(e) => updateItem({ badge_label: e.target.value })}
            placeholder={translate('catalog.badge_label_placeholder')}
            sx={{ mb: 1.5 }}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={translate('catalog.display_description')}
            value={item.display_description || ''}
            disabled={disabled}
            onChange={(e) => updateItem({ display_description: e.target.value })}
            sx={{ mb: 1.5 }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={item.sku_selection_mode !== 'SELECTED'}
                disabled={disabled}
                onChange={(e) => toggleSkuMode(e.target.checked)}
              />
            }
            label={translate('catalog.all_active_variations')}
            sx={{ mb: 1, display: 'block' }}
          />
          {item.sku_selection_mode !== 'SELECTED' && (
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>{translate('catalog.price_mode')}</InputLabel>
              <Select
                label={translate('catalog.price_mode')}
                value={item.price_display_mode || 'SHOW'}
                disabled={disabled}
                onChange={(e) => updateItem({ price_display_mode: e.target.value })}
              >
                <MenuItem value="SHOW">{translate('catalog.price_display_show')}</MenuItem>
                <MenuItem value="ON_REQUEST">{translate('catalog.price_display_on_request')}</MenuItem>
              </Select>
            </FormControl>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {translate('catalog.choose_option')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {(item.resolved_skus || []).map((v) => {
              const isSelected =
                item.sku_selection_mode === 'ALL_ACTIVE' ||
                (item.skus || []).some((s) => s.sku_id === v.id);
              return (
                <Chip
                  key={v.id}
                  size="small"
                  label={resolvedSkuDisplayLabel(v)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  disabled={disabled || item.sku_selection_mode === 'ALL_ACTIVE'}
                  onClick={() => {
                    if (item.sku_selection_mode === 'SELECTED') {
                      toggleVariation(v.id, !isSelected);
                    }
                  }}
                />
              );
            })}
          </Box>
          {item.sku_selection_mode === 'SELECTED' &&
            (item.skus || []).map((sk) => (
              <Box key={sk.sku_id} sx={{ pl: 1, mb: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                <Typography variant="caption" fontWeight={600}>
                  {sk._label || sk.sku_id}
                </Typography>
                <FormControl fullWidth size="small" sx={{ mt: 0.5, mb: 0.5 }}>
                  <InputLabel>{translate('catalog.price_mode')}</InputLabel>
                  <Select
                    label={translate('catalog.price_mode')}
                    value={sk.price_mode || 'INHERIT'}
                    disabled={disabled}
                    onChange={(e) => updateCatalogSku(sk.sku_id, { price_mode: e.target.value })}
                  >
                    <MenuItem value="INHERIT">{translate('catalog.price_mode_inherit')}</MenuItem>
                    <MenuItem value="FIXED">{translate('catalog.price_mode_fixed')}</MenuItem>
                    <MenuItem value="RANGE">{translate('catalog.price_mode_range')}</MenuItem>
                    <MenuItem value="ON_REQUEST">{translate('catalog.price_mode_on_request')}</MenuItem>
                  </Select>
                </FormControl>
                {sk.price_mode === 'FIXED' && (
                  <TextField
                    size="small"
                    type="number"
                    label={translate('catalog.override_price')}
                    value={sk.override_price ?? ''}
                    disabled={disabled}
                    onChange={(e) =>
                      updateCatalogSku(sk.sku_id, {
                        override_price: e.target.value === '' ? null : parseFloat(e.target.value),
                      })
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                    fullWidth
                  />
                )}
                {sk.price_mode === 'RANGE' && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label={translate('catalog.min_price')}
                      value={sk.min_price ?? ''}
                      disabled={disabled}
                      onChange={(e) =>
                        updateCatalogSku(sk.sku_id, {
                          min_price: e.target.value === '' ? null : parseFloat(e.target.value),
                        })
                      }
                      inputProps={{ min: 0, step: 0.01 }}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={translate('catalog.max_price')}
                      value={sk.max_price ?? ''}
                      disabled={disabled}
                      onChange={(e) =>
                        updateCatalogSku(sk.sku_id, {
                          max_price: e.target.value === '' ? null : parseFloat(e.target.value),
                        })
                      }
                      inputProps={{ min: 0, step: 0.01 }}
                      fullWidth
                    />
                  </Box>
                )}
              </Box>
            ))}
          <CatalogItemMediaEditor
            item={item}
            productId={item.product_id}
            disabled={disabled}
            onChange={onChange}
          />
        </>
      )}
    </Box>
  );
};

export default CatalogProductItemForm;
